import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  IOffersFragment,
  QuestIncentiveType,
  useLoyaltyQuestsQuery,
  useLoyaltyUserOffersQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyOfferConfigsQuery } from 'generated/sanity-graphql';
import { useConvertPluConfigs } from 'hooks/use-convert-plu-configs';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { IUseLoyaltyQuests, UseLoyaltyQuests } from 'state/loyalty/hooks/types';
import { StorageKeys } from 'utils/local-storage';

enum QuestStatus {
  ACTIVATED = 'ACTIVATED',
  NOT_ENROLLED = 'NOT_ENROLLED',
  COMPLETED = 'COMPLETED',
  NOT_COMPLETED = 'NOT_COMPLETED',
}

type UserOffersType = readonly ({
  readonly __typename?: 'Offer';
} & IOffersFragment)[];

export interface IUnlockedIncentiveModalStatus {
  unlockedBenefitsIds?: string[];
  showUnlockedOfferModalStatus?: boolean;
}

export const useLoyaltyQuests: UseLoyaltyQuests = (loyaltyId): IUseLoyaltyQuests => {
  const {
    data,
    loading: questsLoading,
    refetch: refetchLoyaltyQuest,
  } = useLoyaltyQuestsQuery({
    variables: { loyaltyId },
    skip: !loyaltyId,
  });

  const { cloneAndConvertDeep } = useConvertPluConfigs();
  const { data: userLoyaltyOffers, refetch: refetchUsersOffers } = useLoyaltyUserOffersQuery({
    variables: {
      loyaltyId: loyaltyId ?? '',
    },
    skip: !loyaltyId,
  });
  const { enableInvitationCode, featureInvitationCode } = useFeatureInvitationCode();

  const [challengeIncentiveSelected, setChallengeIncentiveSelected] = useState<boolean | undefined>(
    false
  );
  const [unlockedBenefits, setUnlockedBenefits] = useState<UserOffersType>([]);
  const [unlockedIncentivesModalVisibilityStatus, setUnlockedIncentivesModalVisibilityStatus] =
    useState<IUnlockedIncentiveModalStatus>(() => {
      const currentStatus = localStorage.getItem(StorageKeys.QUEST_COMPLETED_UNLOCKED_INCENTIVE);

      return currentStatus && JSON.parse(currentStatus);
    });
  const [signUpModalOfferVisibility, setSignUpModalOfferVisibility] = useState<boolean | undefined>(
    () => {
      const currentStatus =
        localStorage.getItem(StorageKeys.INVITATION_CODE_SIGN_UP_MODAL) ?? undefined;

      return currentStatus && JSON.parse(currentStatus);
    }
  );

  const onChallengeIcentiveSelect = useCallback(
    (challengeIncentiveSelected: boolean | undefined) => {
      setChallengeIncentiveSelected(challengeIncentiveSelected);
    },
    []
  );

  const userAvailableQuests =
    data?.loyaltyQuests?.filter(quest => quest.status !== QuestStatus.COMPLETED) || [];

  // For the first release we'll be considering only offers
  // Change it later when rewards and points become available.
  const loyaltyOffersId = userAvailableQuests
    ?.filter(quest => quest.incentiveType === QuestIncentiveType.OFFER)
    .map(quest => quest.incentiveId);

  let challengeIncentives;

  const skipLoyaltyConfigOffersQuery = !Boolean(loyaltyOffersId.length);

  const { data: configOffers } = useLoyaltyOfferConfigsQuery({
    variables: { ids: loyaltyOffersId as Array<string> },
    skip: skipLoyaltyConfigOffersQuery,
  });

  challengeIncentives = useMemo(() => {
    // avoid re-applying clone and convert deep
    return cloneAndConvertDeep?.(configOffers?.allConfigOffer) ?? configOffers?.allConfigOffer;
  }, [configOffers, cloneAndConvertDeep]);

  const onChangeUnlockedIncentivesModalVisibilityStatus = useCallback(
    ({
      unlockedBenefitsIds,
      showUnlockedOfferModalStatus = false,
    }: IUnlockedIncentiveModalStatus) => {
      const updatedStatus = {
        unlockedBenefitsIds: unlockedBenefitsIds
          ? unlockedBenefitsIds
          : unlockedIncentivesModalVisibilityStatus.unlockedBenefitsIds,
        showUnlockedOfferModalStatus,
      };
      setUnlockedIncentivesModalVisibilityStatus(updatedStatus);
    },
    [setUnlockedIncentivesModalVisibilityStatus, unlockedIncentivesModalVisibilityStatus]
  );

  const onChangeSignUpModalOfferVisibility = useCallback(
    (visibility: boolean) => {
      setSignUpModalOfferVisibility(visibility);
      localStorage.setItem(StorageKeys.INVITATION_CODE_SIGN_UP_MODAL, String(visibility));
    },
    [setSignUpModalOfferVisibility]
  );

  useEffect(() => {
    if (enableInvitationCode) {
      const userOffers = userLoyaltyOffers?.loyaltyUser?.offers;

      if (userOffers) {
        const invitationCodeBenefitOfferId =
          featureInvitationCode?.invitationCodeBenefit?.[0]?.incentive?.offer?.loyaltyEngineId;

        const unlockedBenefits = userOffers.filter(
          offer => offer.configId === invitationCodeBenefitOfferId
        );

        if (!unlockedBenefits) {
          return;
        }

        setUnlockedBenefits(unlockedBenefits);
      }
    }
  }, [userLoyaltyOffers, enableInvitationCode, featureInvitationCode]);

  useEffect(() => {
    if (!unlockedBenefits.length) {
      return;
    }

    // For users who have invitated his buddy, we check wheter
    // there is new unlocked benefits to trigger the modal
    const unlockedBenefitsIds = unlockedBenefits.map(benefit => benefit.id);

    const currentStatus = localStorage.getItem(StorageKeys.QUEST_COMPLETED_UNLOCKED_INCENTIVE);

    const unlockedIncentivesModalVisibilityStatus = currentStatus && JSON.parse(currentStatus);

    const newUnlockedBenefitsIds = unlockedBenefitsIds?.filter(
      id => !unlockedIncentivesModalVisibilityStatus?.unlockedBenefitsIds?.includes(id)
    );

    if (newUnlockedBenefitsIds.length) {
      const updatedUnlockedBenefitsIds = [
        ...(unlockedIncentivesModalVisibilityStatus?.unlockedBenefitsIds || []),
        ...newUnlockedBenefitsIds,
      ];
      onChangeUnlockedIncentivesModalVisibilityStatus({
        unlockedBenefitsIds: updatedUnlockedBenefitsIds,
        showUnlockedOfferModalStatus: true,
      });
    } else {
      setUnlockedIncentivesModalVisibilityStatus(prevState => ({
        ...prevState,
        showUnlockedOfferModalStatus: false,
      }));
    }
  }, [unlockedBenefits]);

  useEffect(() => {
    if (!loyaltyId) {
      return;
    }
    // Refetching to ensure quests have the updated status in case
    // the user has completed chagelleges in the app
    refetchLoyaltyQuest({ loyaltyId });

    refetchUsersOffers({ loyaltyId });
  }, [loyaltyId, refetchLoyaltyQuest, refetchUsersOffers]);

  // Syncing localStorage data
  useEffect(() => {
    localStorage.setItem(
      StorageKeys.QUEST_COMPLETED_UNLOCKED_INCENTIVE,
      JSON.stringify(unlockedIncentivesModalVisibilityStatus)
    );
  }, [unlockedIncentivesModalVisibilityStatus]);

  return {
    challengeIncentives,
    challengeIncentiveSelected,
    questsLoading,
    userAvailableQuests,
    onChallengeIcentiveSelect,
    onChangeUnlockedIncentivesModalVisibilityStatus,
    onChangeSignUpModalOfferVisibility,
    unlockedIncentivesModalVisibilityStatus,
    signUpModalOfferVisibility,
  };
};
